"use client"

import { useTranslations } from "next-intl"

import { Link } from "@i18n/navigation"
import { Button } from "@/components/ui/button"

import { useConsent } from "./context"

/**
 * Cookie-consent banner. Shown only while the visitor has not decided; the
 * "accept all" branch is what allows optional analytics to load (the Google
 * Analytics component checks the same consent state), so nothing optional ever
 * runs before an explicit choice. Wording mirrors the Cookie Policy.
 */
export default function ConsentBanner() {
  const t = useTranslations("Consent")
  const { ready, choice, decide } = useConsent()

  // SSR and the first client paint render nothing: the stored choice is only
  // known on the client, and a banner that flashes for decided visitors is
  // worse than one that appears a frame late.
  if (!ready || choice !== null) {
    return null
  }

  return (
    <div
      role="region"
      aria-label={t("title")}
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-ui-border-base bg-ui-bg-base/95 backdrop-blur"
      data-testid="consent-banner"
    >
      <div className="content-container flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between md:gap-8">
        <div className="max-w-3xl">
          <p className="txt-compact-medium-plus text-ui-fg-base">{t("title")}</p>
          <p className="mt-1 txt-compact-small text-ui-fg-subtle">
            {t("body")}{" "}
            <Link
              href="/legal/cookies"
              className="text-ui-fg-base underline underline-offset-4 hover:text-ui-fg-subtle"
            >
              {t("policyLink")}
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => decide("essential")}
            data-testid="consent-essential"
          >
            {t("essentialOnly")}
          </Button>
          <Button
            size="sm"
            onClick={() => decide("all")}
            data-testid="consent-accept-all"
          >
            {t("acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  )
}
